import React, { useState } from 'react';
import { makeStyles, useTheme } from '@material-ui/core/styles';

import useMediaQuery from '@material-ui/core/useMediaQuery';
import TextField from '@material-ui/core/TextField';
import Switch from '@material-ui/core/Switch';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Paper from '@material-ui/core/Paper';
import Divider from '@material-ui/core/Divider';

import Dialog from '@material-ui/core/Dialog';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';

import IconButton from '@material-ui/core/IconButton';
import CloseIcon from '@material-ui/icons/Close';

import GroupSelect from 'app/components/group/GroupSelect';
import SaveButton from 'app/components/buttons/SaveButton';
import Loading from 'app/components/loaders/Loading';

import useApi from 'app/context/useApi';

import { paper } from 'app/styles/core';

const useStyles = makeStyles(theme => ({
    paper: {
        ...paper(theme),
        maxWidth: '640px',
        marginLeft: 'auto',
        marginRight: 'auto',
    },
    dividerTop: {
        marginTop: theme.spacing(1),
    },
    saveButton: {
        marginTop: theme.spacing(3)
    },
    groupSelect: {
        backgroundColor: theme.palette.grey[100],
        width: '100%',
        height: '400px',
        overflowY: 'scroll',
        borderRadius: theme.shape.borderRadius,
        marginTop: theme.spacing(2)
    },
    marginTop: {
        marginTop: theme.spacing(3)
    },
    closeButton: {
        position: 'absolute',
        right: theme.spacing(1),
        top: theme.spacing(1),
        color: theme.palette.grey[500],
    },
    dialogPaper: {
        overflowY: 'initial'
    },
    height: {
        height: '100%'
    },
    content: {
        height: '100%',
        padding: '0'
    }
}));

const defaultWelcome = "Welcome to your member portal. From here you can catch up on news, message members, view events and much more. We're glad you are here.";

export default function NewUserDialog(props) {

    const { levels, levelsLoading, onSave, open, onClose } = props;

    const [ email, setEmail ] = useState("");
    const [ username, setUsername ] = useState("");
    const [ first, setFirst ] = useState("");
    const [ last, setLast ] = useState("");
    const [ password, setPassword ] = useState("");
    const [ passwordConfirmation, setPasswordConfirmation ] = useState("");
    const [ levelId, setLevelId ] = useState(0);
    const [ welcome, setWelcome ] = useState(defaultWelcome);

    const [ notifyUser, setNotifyUser ] = useState(false);
    const [ errors, setErrors ] = useState({});
    const [ loading, setLoading ] = useState(false);

    const classes = useStyles();
    const api = useApi();
    const theme = useTheme();

    const mobile = useMediaQuery(theme.breakpoints.down('sm'));

    const handleEmail = e => {
        const value = e.target.value;

        if (isError("email")) delete errors.email;

        setEmail(value);
    };

    const handleUsername = e => {

        const value = e.target.value;

        if (isError("username")) delete errors.username;

        setUsername(value);
    };

    const handleFirst= e => {
        const value = e.target.value;

        if (isError("first")) delete errors.first;

        setFirst(value);
    };

    const handleLast = e => {
        const value = e.target.value;

        if (isError("last")) delete errors.last;

        setLast(value);
    };

    const handlePassword = e => {
        const value = e.target.value;

        if (isError("password")) delete errors.password;

        setPassword(value);
    };

    const handleWelcome = e => {
        const value = e.target.value;

        if (isError("welcome")) delete errors.welcome;

        setWelcome(value);
    };

    const handlePasswordConfirmation = e => {
        const value = e.target.value;
        setPasswordConfirmation(value);
    };

    const handleNotifyUser = e => {
        setNotifyUser(e.target.checked);
    };

    const handleLevelId = id => {
        setLevelId(id);
    };

    const postUsernameValidate = () => {

        const data = {
            username: username
        };

        api.post('/auth/username/validate', data).catch(error => {
            if (error?.response?.data?.errors) {
                setErrors(error.response.data.errors)
            }
        });
    }

    const postNewUser = () => {
        setLoading(true);

        const data = {
            email: email,
            username: username,
            first: first,
            last: last,
            password: password,
            password_confirmation: passwordConfirmation,
            level_id: levelId,
            notify_user: notifyUser,
            welcome: welcome
        }

        api.post('/admin/user/new', data)
            .then(res => onSave())
            .catch(error => {
                if (error?.response?.data?.errors) setErrors(error.response.data.errors)
            }).finally(() => setLoading(false));
    }

    const isError = field => {
        if (errors[field]) return true;
        return false;
    }

    const getErrorMessage = field => {
        if (isError(field)) return errors[field];
        return "";
    }

    const checkPassword = () => {
        return (password === passwordConfirmation)
    }

    const getUsernameMessage = () => {
        if (isError('username')) return errors.username;
        return "Please do not use any spaces."
    }

    const getPasswordMessage = () => {
        if (isError('username')) return errors.password;
        return "Must include atleast one uppercase letter, lowercase letter, number and special character."
    }

    const getWelcomeMessage = () => {
        if (isError('welcome')) return errors.welcome;
        return "Message displayed to user the first time they log in."
    }

    const handleClosed = () => {
        setEmail("");
        setUsername("");
        setFirst("");
        setLast("");
        setPassword("");
        setPasswordConfirmation("");
        setLevelId(0);
        setWelcome(defaultWelcome);
        setNotifyUser(false);
        setErrors({});
        setLoading(false);
    }

    return (
        <Dialog
            open={open}
            onClose={onClose}
            // onExited={onClosed}
            maxWidth={mobile ? null : 'sm'}
            fullScreen={mobile ? true : false}
            aria-labelledby="new-user"
            classes={{paperScrollPaper: classes.dialogPaper}}
            TransitionProps={{
                onExited: handleClosed
            }}
            >
            <Loading isLoading={loading}/>
            <DialogTitle id="new-user">
                <span>New User</span>
                <IconButton aria-label="Cancel" className={classes.closeButton} onClick={onClose}>
                    <CloseIcon />
                </IconButton>
            </DialogTitle>
            <DialogContent dividers className={classes.content}>
                <form noValidate autoComplete="off">
                    <Paper className={classes.paper}>
                        <Loading isLoading={loading}/>
                        <TextField
                            id="input-email"
                            label="Email"
                            name="email"
                            value={email}
                            margin="normal"
                            onChange={handleEmail}
                            fullWidth
                            autoFocus
                            error={isError('email')}
                            helperText={getErrorMessage('email')}
                            variant="outlined"
                            className={classes.marginTop}
                        />
                        <TextField
                            id="input-username"
                            label="Username"
                            name="username"
                            value={username}
                            margin="normal"
                            onChange={handleUsername}
                            fullWidth
                            error={isError('username')}
                            helperText={getUsernameMessage()}
                            variant="outlined"
                            onBlur={postUsernameValidate}
                        />
                        <Divider className={classes.dividerTop}/>
                        <TextField
                            id="input-first"
                            label="First Name"
                            name="first"
                            value={first}
                            margin="normal"
                            onChange={handleFirst}
                            fullWidth
                            error={isError('first')}
                            helperText={getErrorMessage('first')}
                            variant="outlined"
                        />
                        <TextField
                            id="input-last"
                            label="Last Name"
                            name="last"
                            value={last}
                            margin="normal"
                            onChange={handleLast}
                            fullWidth
                            error={isError('last')}
                            helperText={getErrorMessage('last')}
                            variant="outlined"
                        />
                        <Divider className={classes.dividerTop}/>
                        <TextField
                            id="input-password"
                            label="Password"
                            value={password}
                            onChange={handlePassword}
                            fullWidth
                            type="password"
                            error={isError('password')}
                            helperText={getPasswordMessage()}
                            variant="outlined"
                            margin="normal"
                        />
                        <TextField
                            id="input-check-password"
                            label="Confirm Password"
                            value={passwordConfirmation}
                            onChange={handlePasswordConfirmation}
                            fullWidth
                            type="password"
                            error={!checkPassword()}
                            helperText={checkPassword() ? "" : "Passwords do not match"}
                            variant="outlined"
                            margin="normal"
                        />
                        <Divider className={classes.dividerTop}/>
                        <div className={classes.groupSelect}>
                            <GroupSelect
                                variant="level"
                                label="Select the Users Membership Level"
                                onSelect={handleLevelId}
                                initGroupOptions={levels}
                                optionsLoading={levelsLoading}
                            />
                        </div>
                        <Divider className={classes.dividerTop}/>
                        <FormControlLabel
                            control={
                                <Switch
                                    checked={notifyUser}
                                    onChange={handleNotifyUser}
                                    color="primary"
                                    value="notifyUser"
                                />
                            }
                            label="Notify user by email about their new account?"
                            labelPlacement="start"
                            margin="normal"
                        />
                        <TextField
                            multiline
                            rows="4"
                            rowsMax="4"
                            value={welcome}
                            onChange={handleWelcome}
                            fullWidth
                            error={isError('welcome')}
                            helperText={getWelcomeMessage()}
                            variant="outlined"
                            label="New User Welcome Message"
                            margin="normal"
                            maxlength="191"
                        />
                        <SaveButton
                            disabled={loading}
                            variant="contained"
                            color="primary"
                            onClick={postNewUser}
                            fullWidth
                            className={classes.saveButton}
                            >
                            Create User
                        </SaveButton>
                    </Paper>
                </form>
            </DialogContent>
            {/* <DialogActions>
                <Button onClick={handleBack}>
                </Button>
                <Button
                    onClick={handleNext}
                    variant="contained"
                    color='primary'
                    >
                </Button>
           </DialogActions> */}
        </Dialog>
    )
}
